import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CheckCircle, XCircle, Clock, Loader2, RefreshCw, ArrowLeft,
  Sprout, Building2, User, Calendar, MapPin, ChevronDown, ChevronUp, Search
} from 'lucide-react';
import { profileRequestAPI } from '../../services/api';

const statusStyles = {
  pending:  { bg: 'bg-yellow-100', text: 'text-yellow-800', border: 'border-yellow-200', icon: <Clock className="w-3.5 h-3.5" /> },
  approved: { bg: 'bg-green-100',  text: 'text-green-800',  border: 'border-green-200',  icon: <CheckCircle className="w-3.5 h-3.5" /> },
  rejected: { bg: 'bg-red-100',    text: 'text-red-800',    border: 'border-red-200',    icon: <XCircle className="w-3.5 h-3.5" /> },
};

function StatusBadge({ status }) {
  const s = statusStyles[status] || statusStyles.pending;
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold border ${s.bg} ${s.text} ${s.border}`}>
      {s.icon}
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
}

function RequestCard({ request, adminId, onRefresh }) {
  const [expanded, setExpanded] = useState(false);
  const [processingAction, setProcessingAction] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [showRejectInput, setShowRejectInput] = useState(false);
  const [localMsg, setLocalMsg] = useState('');
  const isFarmer = request.role === 'farmer';
  const fd = request.formData || {};

  const handleApprove = async () => {
    if (!window.confirm(`Approve this ${isFarmer ? 'farmer' : 'processor'} posting request?\n\nThis will publish their profile to the public directory.`)) return;
    setProcessingAction('approve');
    setLocalMsg('');
    try {
      const res = await profileRequestAPI.approve(request.id, adminId);
      if (res.success) {
        setLocalMsg('✅ Approved! Profile is now live in the public directory.');
        setTimeout(onRefresh, 1500);
      } else {
        setLocalMsg(`⚠️ ${res.message}`);
      }
    } catch (err) {
      setLocalMsg(`❌ ${err.message}`);
    } finally {
      setProcessingAction(null);
    }
  };

  const handleReject = async () => {
    if (!rejectionReason.trim()) {
      setLocalMsg('Please enter a reason for rejection.');
      return;
    }
    if (!window.confirm('Reject this posting request?')) return;
    setProcessingAction('reject');
    setLocalMsg('');
    try {
      const res = await profileRequestAPI.reject(request.id, adminId, rejectionReason);
      if (res.success) {
        setLocalMsg('Request rejected.');
        setTimeout(onRefresh, 1500);
      } else {
        setLocalMsg(`⚠️ ${res.message}`);
      }
    } catch (err) {
      setLocalMsg(`❌ ${err.message}`);
    } finally {
      setProcessingAction(null);
      setShowRejectInput(false);
    }
  };

  return (
    <div className={`bg-white rounded-2xl border shadow-sm overflow-hidden transition-all duration-200 ${
      request.status === 'pending' ? 'border-yellow-200' : request.status === 'approved' ? 'border-green-200' : 'border-red-200'
    }`}>
      <div className="px-5 py-4 flex items-start justify-between gap-4">
        <div className="flex items-center gap-3 min-w-0">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${isFarmer ? 'bg-green-100' : 'bg-amber-100'}`}>
            {isFarmer ? <Sprout className="w-5 h-5 text-green-600" /> : <Building2 className="w-5 h-5 text-amber-600" />}
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="font-semibold text-gray-900 text-sm">
                {isFarmer
                  ? `${fd.lastname || ''}, ${fd.firstname || ''} ${fd.middleInitial || ''}`.trim() || request.userFullname
                  : fd.name || request.userFullname
                }
              </p>
              <StatusBadge status={request.status} />
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${isFarmer ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700'}`}>
                {isFarmer ? '🌱 Farmer' : '🏭 Processor'}
              </span>
            </div>
            <div className="flex items-center gap-3 mt-1 text-xs text-gray-500 flex-wrap">
              <span className="flex items-center gap-1"><User className="w-3 h-3" />{request.username}</span>
              {request.municipality && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{request.municipality}</span>}
              <span className="flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                {request.submittedAt ? new Date(request.submittedAt).toLocaleDateString('en-PH', { year: 'numeric', month: 'short', day: 'numeric' }) : '—'}
              </span>
            </div>
          </div>
        </div>

        <button
          onClick={() => setExpanded(!expanded)}
          className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-800 flex-shrink-0 mt-1 transition"
        >
          {expanded ? <><ChevronUp className="w-4 h-4" />Hide</> : <><ChevronDown className="w-4 h-4" />View Details</>}
        </button>
      </div>

      {expanded && (
        <div className="px-5 pb-4 border-t border-gray-50 pt-3">
          <div className="text-xs text-gray-500 mb-3 flex items-center gap-2">
            <CheckCircle className="w-3.5 h-3.5 text-green-500" />
            Agreed to public disclosure on{' '}
            {request.agreedAt ? new Date(request.agreedAt).toLocaleString('en-PH') : '—'}
          </div>

          {isFarmer ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs text-gray-700">
              {fd.sitioPurok && <div><span className="text-gray-400">Sitio/Purok:</span> {fd.sitioPurok}</div>}
              {fd.age && <div><span className="text-gray-400">Age:</span> {fd.age}</div>}
              {fd.farmData?.farmArea && <div><span className="text-gray-400">Farm Area:</span> {fd.farmData.farmArea} ha</div>}
              {fd.farmData?.varietyPlanted && <div><span className="text-gray-400">Variety:</span> {fd.farmData.varietyPlanted}</div>}
              {fd.incomeData?.annualFarmIncome && <div><span className="text-gray-400">Annual Income:</span> ₱{Number(fd.incomeData.annualFarmIncome).toLocaleString()}</div>}
              {fd.membershipData?.affiliationName && <div><span className="text-gray-400">Affiliation:</span> {fd.membershipData.affiliationName}</div>}
              {fd.productivityData?.weightOfBeansPerHectareKg && <div><span className="text-gray-400">Beans/ha:</span> {fd.productivityData.weightOfBeansPerHectareKg} kg</div>}
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs text-gray-700">
              {fd.address && <div><span className="text-gray-400">Address:</span> {fd.address}</div>}
              {fd.contact && <div><span className="text-gray-400">Contact:</span> {fd.contact}</div>}
              {fd.owner && <div><span className="text-gray-400">Owner:</span> {fd.owner}</div>}
              {fd.years && <div><span className="text-gray-400">Years in Op:</span> {fd.years}</div>}
              {fd.employees && <div><span className="text-gray-400">Employees:</span> {fd.employees}</div>}
              {fd.businessType && <div><span className="text-gray-400">Type:</span> {fd.businessType}</div>}
              {fd.marketingData?.productType && <div><span className="text-gray-400">Products:</span> {fd.marketingData.productType}</div>}
              {fd.processingData?.cacaoQty && <div><span className="text-gray-400">Cacao Qty:</span> {fd.processingData.cacaoQty} kg</div>}
            </div>
          )}

          {request.status === 'rejected' && request.rejectionReason && (
            <div className="mt-3 bg-red-50 border border-red-100 rounded-lg px-3 py-2 text-xs text-red-700">
              <strong>Rejection Reason:</strong> {request.rejectionReason}
            </div>
          )}

          {localMsg && (
            <div className="mt-3 text-sm text-gray-700 bg-gray-50 rounded-lg px-3 py-2">{localMsg}</div>
          )}

          {request.status === 'pending' && (
            <div className="mt-4 flex flex-col sm:flex-row gap-2">
              <button
                id={`btn-approve-request-${request.id}`}
                onClick={handleApprove}
                disabled={!!processingAction}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-semibold rounded-xl disabled:opacity-50 transition"
              >
                {processingAction === 'approve' ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                Approve & Publish
              </button>

              {!showRejectInput ? (
                <button
                  id={`btn-reject-request-${request.id}`}
                  onClick={() => setShowRejectInput(true)}
                  disabled={!!processingAction}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 border border-red-200 text-red-600 hover:bg-red-50 text-sm font-semibold rounded-xl disabled:opacity-50 transition"
                >
                  <XCircle className="w-4 h-4" />
                  Reject
                </button>
              ) : (
                <div className="flex-1 flex flex-col gap-2">
                  <input
                    type="text"
                    value={rejectionReason}
                    onChange={e => setRejectionReason(e.target.value)}
                    placeholder="Reason for rejection (required)"
                    className="w-full px-3 py-2 border border-red-200 rounded-lg text-sm focus:ring-2 focus:ring-red-300 outline-none"
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={handleReject}
                      disabled={!!processingAction}
                      className="flex-1 flex items-center justify-center gap-1.5 px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-semibold rounded-xl disabled:opacity-50 transition"
                    >
                      {processingAction === 'reject' ? <Loader2 className="w-4 h-4 animate-spin" /> : <XCircle className="w-4 h-4" />}
                      Confirm Reject
                    </button>
                    <button
                      onClick={() => { setShowRejectInput(false); setRejectionReason(''); }}
                      className="px-3 py-2 text-gray-500 hover:text-gray-700 text-sm rounded-xl border border-gray-200 hover:bg-gray-50 transition"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function PendingProfileRequests({ user }) {
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');
  const [roleFilter, setRoleFilter] = useState('all');
  const [search, setSearch] = useState('');

  const loadRequests = async () => {
    setIsLoading(true);
    setError('');
    try {
      const res = await profileRequestAPI.getAll(filter !== 'all' ? filter : null);
      if (res.success) {
        setRequests(res.requests || []);
      } else {
        setError('Failed to load posting requests.');
      }
    } catch (err) {
      setError(err.message || 'Failed to load posting requests.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { loadRequests(); }, [filter]);

  const filtered = requests.filter(r => {
    if (roleFilter !== 'all' && r.role !== roleFilter) return false;
    if (search.trim()) {
      const q = search.toLowerCase();
      const name = (r.userFullname || '').toLowerCase();
      const username = (r.username || '').toLowerCase();
      const bizName = (r.formData?.name || '').toLowerCase();
      if (!name.includes(q) && !username.includes(q) && !bizName.includes(q)) return false;
    }
    return true;
  });

  const counts = {
    all: requests.length,
    pending: requests.filter(r => r.status === 'pending').length,
    approved: requests.filter(r => r.status === 'approved').length,
    rejected: requests.filter(r => r.status === 'rejected').length,
  };

  const filters = [
    { key: 'all', label: 'All', count: counts.all },
    { key: 'pending', label: 'Pending', count: counts.pending },
    { key: 'approved', label: 'Approved', count: counts.approved },
    { key: 'rejected', label: 'Rejected', count: counts.rejected },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 to-green-50 p-4 md:p-6">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <button
            onClick={() => navigate('/profile')}
            className="text-gray-500 hover:text-gray-800 transition p-1.5 rounded-lg hover:bg-white"
            aria-label="Back to profile"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Profile Posting Requests</h1>
            <p className="text-sm text-gray-500 mt-0.5">Review and approve farmer/processor public directory listing requests</p>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-3 mb-6">
          {[
            { label: 'Pending', value: counts.pending, color: 'text-yellow-700', bg: 'bg-yellow-50 border-yellow-200' },
            { label: 'Approved', value: counts.approved, color: 'text-green-700', bg: 'bg-green-50 border-green-200' },
            { label: 'Rejected', value: counts.rejected, color: 'text-red-700', bg: 'bg-red-50 border-red-200' },
          ].map(s => (
            <div key={s.label} className={`rounded-xl border p-4 text-center ${s.bg}`}>
              <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
              <p className={`text-xs font-medium ${s.color} opacity-80`}>{s.label}</p>
            </div>
          ))}
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 mb-4 flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
          <div className="flex gap-1.5 flex-wrap">
            {filters.map(f => (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition ${
                  filter === f.key
                    ? 'bg-amber-600 text-white shadow-sm'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                {f.label}
                {f.count > 0 && (
                  <span className={`ml-1.5 rounded-full px-1.5 text-xs ${filter === f.key ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-600'}`}>
                    {f.count}
                  </span>
                )}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-2 w-full sm:w-auto">
            <select
              value={roleFilter}
              onChange={e => setRoleFilter(e.target.value)}
              className="text-xs px-3 py-2 border border-gray-200 rounded-lg bg-white focus:ring-2 focus:ring-amber-400 outline-none"
            >
              <option value="all">All Roles</option>
              <option value="farmer">🌱 Farmers</option>
              <option value="processor">🏭 Processors</option>
            </select>

            <div className="relative flex-1 sm:w-48">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search by name..."
                className="w-full pl-8 pr-3 py-2 border border-gray-200 rounded-lg text-xs focus:ring-2 focus:ring-amber-400 outline-none"
              />
            </div>

            <button
              onClick={loadRequests}
              disabled={isLoading}
              className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 transition disabled:opacity-50"
              title="Refresh"
            >
              <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        )}

        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-20 text-gray-500">
            <Loader2 className="w-8 h-8 animate-spin mb-3 text-amber-500" />
            <p className="text-sm">Loading requests...</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm flex flex-col items-center justify-center py-20 text-gray-400">
            <Clock className="w-12 h-12 mb-3 text-gray-300" />
            <p className="text-sm font-medium text-gray-500">No requests found</p>
            <p className="text-xs text-gray-400 mt-1">
              {filter !== 'all' ? `No ${filter} requests at this time.` : 'No posting requests have been submitted yet.'}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map(r => (
              <RequestCard
                key={r.id}
                request={r}
                adminId={user?.id}
                onRefresh={loadRequests}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
